import { useId, useState } from "react";
import { OrganizationalRecordPerson } from "./OrganizationalRecordPerson";
import { FormattedMessage, useIntl } from "react-intl";
import { useMutation, useQuery } from "@apollo/client/react";
import { Button } from "@/components/design/Button";
import { BoxHeader } from "@/components/design/BoxHeader";
import { TextInput, TextArea } from "@/components/design/TextField";
import { Select } from "@/components/design/Select";
import { EditPanelButtons } from "@/components/design/EditPanelButtons";
import { Collapse } from "@/components/design/Collapse";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { DeleteOrganizationalRecordEntryDocument, OrganizationalRecordEntriesDocument, OrganizationalRecordEntryKind,
  OrganizationalRecordItemKind, type OrganizationalRecordEntriesQuery } from "@/graphql/generated/operations";
import { formatDateTime } from "@/lib/format-date";
import { userErrorMessage } from "@/lib/user-error";
import { OrganizationalRecordComments } from "./OrganizationalRecordComments";
import { useOrganizationalRecordAction } from "./use-organizational-record-action";
import { OrganizationalRecordLinkEditor, OrganizationalRecordLinkList, type OrganizationalRecordNavigate } from "./OrganizationalRecordLinks";
import { useOrganizationalRecordDrafts, isOrganizationalRecordDraftDirty, type OrganizationalRecordDraftValue, type OrganizationalRecordDraft } from "./OrganizationalRecordDrafts";

type Entry = OrganizationalRecordEntriesQuery["organizationalRecordEntries"]["nodes"][number];
const initialEntry = (entry?: Entry): OrganizationalRecordDraftValue => ({ type: "entry", id: entry?.id, title: entry?.title ?? "",
  kind: entry?.kind ?? OrganizationalRecordEntryKind.Note, content: entry?.content ?? "", links: entry?.links ?? [] });

export function OrganizationalRecordEntries({ recordId, canWrite, onNavigate, embedded = false }: { recordId: string; canWrite: boolean; onNavigate?: OrganizationalRecordNavigate; embedded?: boolean }) {
  const intl = useIntl();
  const input = { recordId, first: 25 };
  const query = useQuery(OrganizationalRecordEntriesDocument, { variables: { input }, fetchPolicy: "no-cache" });
  const [remove] = useMutation(DeleteOrganizationalRecordEntryDocument, { fetchPolicy: "no-cache" });
  const action = useOrganizationalRecordAction();
  const store = useOrganizationalRecordDrafts();
  const [editor, setEditor] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<Entry | null>(null);
  const [discarding, setDiscarding] = useState<string | null>(null);
  const page = query.data?.organizationalRecordEntries;
  const busy = action.busy || store.busy;
  function edit(entry?: Entry) {
    const key = entry ? `entry:${entry.id}` : `entry:new:${crypto.randomUUID()}`;
    store.update(key, initialEntry(entry)); setEditor(key);
  }
  function cancel(key: string) {
    if (store.drafts[key] && isOrganizationalRecordDraftDirty(store.drafts[key])) setDiscarding(key);
    else { store.discard(key); setEditor(null); }
  }
  const pending = Object.entries(store.drafts).filter(([, draft]) => draft.type === "entry" && !draft.id);
  return <div className="space-y-4">
    {(!embedded || canWrite) && <div className={`flex flex-wrap items-center gap-4 ${embedded ? "justify-end" : "justify-between"}`}>
      {!embedded && <BoxHeader title={<FormattedMessage id="organizationalRecord.entries" />} />}
      {canWrite && <Button disabled={busy} onClick={() => edit()}><FormattedMessage id="organizationalRecord.newEntry" /></Button>}
    </div>}
    {(query.error || action.error) && <p role="alert">{action.error ?? userErrorMessage(intl, query.error)}</p>}
    {canWrite && editor && store.drafts[editor] && <EntryEditor recordId={recordId} draft={store.drafts[editor]} busy={busy}
      onChange={patch => store.update(editor, store.drafts[editor]!, patch)} onClose={() => {
        if (!isOrganizationalRecordDraftDirty(store.drafts[editor]!)) store.discard(editor);
        setEditor(null);
      }} onCancel={() => cancel(editor)} />}
    {pending.map(([key, draft]) => key !== editor && <article key={key} className="box space-y-3 bg-surface p-5">
      <h3 className="smycles-field-value font-medium break-words">{draft.title || intl.formatMessage({ id: "organizationalRecord.newEntry" })}</h3>
      <p className="smycles-field-value whitespace-pre-wrap break-words">{draft.content}</p>
      <OrganizationalRecordLinkList links={draft.links} onNavigate={onNavigate} />
      <p className="smycles-field-meta text-ink-muted"><FormattedMessage id="unsavedGuard.title" /></p>
      {store.errors[key] && <p role="alert" className="smycles-field-meta">{store.errors[key]}</p>}
      <div className="flex gap-2"><Button variant="secondary" disabled={busy} onClick={() => setEditor(key)}><FormattedMessage id="common.edit" /></Button>
        <Button variant="secondary" disabled={busy} onClick={() => cancel(key)}><FormattedMessage id="common.discard" /></Button></div>
    </article>)}
    {query.loading && <p><FormattedMessage id="common.loading" defaultMessage="Loading…" /></p>}
    {!query.loading && !query.error && !page?.nodes.length && !pending.length && <p className="smycles-field-value text-ink-muted"><FormattedMessage id="organizationalRecord.noEntries" /></p>}
    {!query.error && page?.nodes.map(entry => {
      const key = `entry:${entry.id}`, draft = store.drafts[key];
      const shown = draft ?? entry;
      return <article key={entry.id} className="box space-y-3 bg-surface p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div><span className="smycles-field-meta text-ink-muted"><FormattedMessage id={shown.kind === OrganizationalRecordEntryKind.Report ? "organizationalRecord.report" : "organizationalRecord.note"} /></span>
            <h3 className="smycles-field-value font-medium break-words">{shown.title}</h3></div>
          <div className="flex flex-wrap gap-2">
            {onNavigate && <Button variant="secondary" onClick={() => onNavigate({ id: entry.id, title: entry.title, kind: OrganizationalRecordItemKind.Entry })}><FormattedMessage id="organizationalRecord.links.details" /></Button>}
            {canWrite && <><Button variant="secondary" disabled={busy} onClick={() => edit(entry)}><FormattedMessage id="common.edit" /></Button>
              <Button variant="secondary" disabled={busy} onClick={() => setDeleting(entry)}><FormattedMessage id="common.delete" /></Button></>}
          </div>
        </div>
        <p className="smycles-field-value whitespace-pre-wrap break-words">{shown.content}</p>
        <OrganizationalRecordLinkList links={shown.links} onNavigate={onNavigate} />
        {draft && isOrganizationalRecordDraftDirty(draft) && <p className="smycles-field-meta text-ink-muted"><FormattedMessage id="unsavedGuard.title" /></p>}
        {store.errors[key] && <p role="alert" className="smycles-field-meta">{store.errors[key]}</p>}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 smycles-field-meta text-ink-muted">
          <OrganizationalRecordPerson person={entry.createdBy} /> · {formatDateTime(entry.createdAt, intl.locale)}
          {entry.updatedAt && <div className="flex flex-wrap items-center gap-x-2 gap-y-1"> · <FormattedMessage id="organizationalRecord.updated" values={{
            name: <OrganizationalRecordPerson person={entry.updatedBy} />, date: formatDateTime(entry.updatedAt, intl.locale),
          }} /></div>}</div>
        <EntryDiscussion recordId={recordId} entryId={entry.id} canWrite={canWrite} onNavigate={onNavigate} />
      </article>;
    })}
    {page?.pageInfo.hasNextPage && <Button variant="secondary" disabled={query.loading || busy} onClick={() => void action.run(() => query.fetchMore({
      variables: { input: { ...input, after: page.pageInfo.endCursor } },
      updateQuery: (previous, { fetchMoreResult: next }) => ({ ...next, organizationalRecordEntries: { ...next.organizationalRecordEntries,
        nodes: [...previous.organizationalRecordEntries.nodes, ...next.organizationalRecordEntries.nodes] } }),
    }))}><FormattedMessage id="common.loadMore" defaultMessage="Load more" /></Button>}
    <ConfirmDialog open={Boolean(deleting)} title={<FormattedMessage id="organizationalRecord.deleteEntry" />} message={<FormattedMessage id="organizationalRecord.deleteEntryHelp" />}
      onCancel={() => { if (!busy) setDeleting(null); }} onConfirm={() => action.run(async () => {
        if (!deleting || store.busy) return;
        await remove({ variables: { id: deleting.id } });
        for (const [key, draft] of Object.entries(store.drafts)) {
          if ((draft.type === "entry" && draft.id === deleting.id) || (draft.type === "comment" && draft.entryId === deleting.id)) store.discard(key);
        }
        setDeleting(null); await query.refetch();
      })} />
    <ConfirmDialog open={Boolean(discarding)} title={<FormattedMessage id="unsavedGuard.title" />} message={<FormattedMessage id="organizationalRecord.drafts.discardEntry" />}
      confirmLabel={<FormattedMessage id="common.discard" />} onCancel={() => setDiscarding(null)} onConfirm={() => {
        if (discarding && !busy) { store.discard(discarding); if (editor === discarding) setEditor(null); setDiscarding(null); }
      }} />
  </div>;
}

function EntryDiscussion({ recordId, entryId, canWrite, onNavigate }: {
  recordId: string; entryId: string; canWrite: boolean; onNavigate?: OrganizationalRecordNavigate;
}) {
  const [open, setOpen] = useState(false);
  const [visited, setVisited] = useState(false);
  const regionId = useId();
  return <div className="border-t border-ink/10 pt-3">
    <Button variant="ghost" size="compact" aria-expanded={open} aria-controls={regionId}
      onClick={() => { setVisited(true); setOpen(value => !value); }}>
      <span aria-hidden className={`inline-block transition-transform duration-[var(--duration-base)] ${open ? "rotate-90" : ""}`}>›</span>
      <FormattedMessage id="share.itemDetail.comments" />
    </Button>
    <Collapse open={open}>
      <div id={regionId} className="box-flat mt-3 max-w-[50rem] bg-page p-4">
        {visited && <OrganizationalRecordComments recordId={recordId} entryId={entryId} canWrite={canWrite} onNavigate={onNavigate} embedded />}
      </div>
    </Collapse>
  </div>;
}

function EntryEditor({ recordId, draft, busy, onChange, onClose, onCancel }: {
  recordId: string; draft: OrganizationalRecordDraft; busy: boolean; onChange: (patch: Partial<OrganizationalRecordDraftValue>) => void; onClose: () => void; onCancel: () => void;
}) {
  const intl = useIntl();
  const headingId = useId();
  const missingFields = [
    !draft.title?.trim() || Array.from(draft.title).length > 255 ? intl.formatMessage({ id: "organizationalRecord.titleField" }) : "",
    !draft.content.trim() || Array.from(draft.content).length > 65535 ? intl.formatMessage({ id: "organizationalRecord.contentField" }) : "",
  ].filter(Boolean);
  return <section aria-labelledby={headingId} className="box space-y-4 bg-surface p-6">
    <BoxHeader title={<h3 id={headingId}><FormattedMessage id={draft.id ? "organizationalRecord.editEntry" : "organizationalRecord.newEntry"} /></h3>} />
    <fieldset disabled={busy} className="space-y-4">
      <label className="block"><span className="smycles-field-label"><FormattedMessage id="organizationalRecord.kind" /></span>
        <Select className="mt-1 max-w-48" value={draft.kind} onChange={kind => onChange({ kind: kind as OrganizationalRecordEntryKind })} hideEmpty disabled={busy}
          options={[{ value: OrganizationalRecordEntryKind.Note, label: intl.formatMessage({ id: "organizationalRecord.note" }) },
            { value: OrganizationalRecordEntryKind.Report, label: intl.formatMessage({ id: "organizationalRecord.report" }) }]} /></label>
      <label className="block"><span className="smycles-field-label"><FormattedMessage id="organizationalRecord.titleField" /></span>
        <TextInput required className="mt-1" value={draft.title ?? ""} disabled={busy} onChange={event => onChange({ title: event.target.value })} /></label>
      <label className="block"><span className="smycles-field-label"><FormattedMessage id="organizationalRecord.contentField" /></span>
        <TextArea required rows={12} className="mt-1" value={draft.content} disabled={busy} onChange={event => onChange({ content: event.target.value })} /></label>
      <OrganizationalRecordLinkEditor recordId={recordId} sourceEntryId={draft.id} value={draft.links} disabled={busy} onChange={links => onChange({ links })} />
      <EditPanelButtons onCancel={onCancel} onConfirm={onClose} missingFields={missingFields} />
    </fieldset>
  </section>;
}
